import {
  addBreadcrumb,
  browserSessionIntegration,
  captureException,
  init,
  isInitialized,
  reactErrorHandler,
  setTag,
  setUser,
  startInactiveSpan,
  tanstackRouterBrowserTracingIntegration,
} from "@sentry/react";
import { supabaseIntegration } from "@supabase/sentry-js-integration";
import { SupabaseClient } from "@supabase/supabase-js";
import posthog from "posthog-js";

import { getEnv } from "@/hooks/useEnv";
import { router } from "@/providers/routerConfig";
import { userName } from "@/utils/format";

import type { RootOptions } from "react-dom/client";

import type { UserProfile, Workspace } from "@/supabase.types";

export const getSentryProjectId = () => {
  const sentryDSN = getEnv("VITE_SENTRY_DSN");
  if (!sentryDSN) return undefined;

  // assume sentry project id is in format: https://<NUMBER>@o<NUMBER>.ingest.us.sentry.io/<NUMBER>
  const projectId = sentryDSN.split("/").slice(-1)[0] ?? undefined;
  return Number(projectId);
};

(() => {
  if (getEnv("VITE_SENTRY_DSN")) {
    init({
      // ⭐ base config
      dsn: getEnv("VITE_SENTRY_DSN"),
      release: getEnv("VITE_RELEASE_TAG"),
      environment: getEnv("VITE_ENV"),
      debug: false,
      // Set sampleRate to 1.0 to capture 100% of all errors.
      sampleRate: 0.2,
      // ! ignores the typical error originating from outdated chunks, see: https://github.com/vitejs/vite/issues/11804
      ignoreErrors: [
        /dynamically imported module/,
        /Failed to fetch/,
        /TypeError: Load failed/,
        /Load failed/,
        /network error/,
        /is not a valid JavaScript MIME type/,
        /Non-Error promise rejection captured/,
      ],
      // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
      tracesSampleRate: 0.05,
      beforeSend(_event, hint) {
        const event = _event;
        // ! ignores the typical error originating from outdated chunks, see: https://github.com/vitejs/vite/issues/11804
        const error = hint.originalException;
        if (error) {
          if (error instanceof Error) {
            if (
              error.message.includes("dynamically imported module") ||
              error.message.includes("Failed to fetch") ||
              error.message.includes("TypeError: Load failed") ||
              error.message.includes("Load failed") ||
              error.message.includes("network error") ||
              error.message.includes("is not a valid JavaScript MIME type") ||
              error.message.includes("Non-Error promise rejection captured")
            ) {
              // event is dropped
              return null;
            }
          } else if (typeof error === "object") {
            // Handle non-Error objects (like Supabase errors) by normalizing them
            const obj = error as Record<string, unknown>;
            const message = typeof obj.message === "string" ? obj.message : JSON.stringify(obj);

            // Create a proper Error instance
            const normalizedError = new Error(message);
            normalizedError.name = typeof obj.code === "string" ? obj.code : "Error";

            // Add additional context as extra data
            event.extra = {
              ...event.extra,
              originalErrorObject: obj,
            };

            // Update the exception in the event
            if (event.exception?.values?.[0]) {
              event.exception.values[0].value = message;
              event.exception.values[0].type = normalizedError.name;
            }
          }
        }
        return event;
      },
      integrations: [
        browserSessionIntegration(),
        supabaseIntegration(
          SupabaseClient,
          {
            addBreadcrumb,
            captureException,
            startInactiveSpan,
          },
          {
            tracing: true,
            breadcrumbs: true,
            errors: true,
          },
        ),

        tanstackRouterBrowserTracingIntegration(router, {
          shouldCreateSpanForRequest: (url) => {
            return !url.startsWith(`${getEnv("VITE_SUPABASE_URL")}/rest`);
          },
        }),

        posthog.sentryIntegration({
          organization: "wakurobotics",
          projectId: getSentryProjectId(),
          severityAllowList: ["error", "fatal", "info", "warning"], // optional: here is set to handle captureMessage (info) and captureException (error)
        }),
        // Note: we don't want and need the replay feature, since it was unstable last time tried
      ],
    });
  }
})();

export const reactRootErrorOptions: RootOptions = {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: reactErrorHandler(),
};

/**
 * Converts Supabase error objects to proper Error instances
 * Supabase errors are plain objects with shape: { code, details, hint, message }
 */
export const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "object" && error !== null) {
    const obj = error as Record<string, unknown>;
    const message = typeof obj.message === "string" ? obj.message : JSON.stringify(obj);
    const normalizedError = new Error(message);
    normalizedError.name = typeof obj.code === "string" ? obj.code : "Error";

    // Preserve original error object for debugging
    normalizedError.cause = obj;

    return normalizedError;
  }

  return new Error(String(error));
};

export const handleErrorWithSentry = (error?: unknown) => {
  if (isInitialized()) {
    captureException(normalizeError(error));
  }
};

export const setSentryUser = (profile?: UserProfile | null) => {
  if (isInitialized() && profile) {
    setUser({
      id: profile.id,
      email: profile.email,
      username: userName(profile),
    });
  }
};
export const clearSentryUser = () => {
  if (isInitialized()) {
    setUser(null);
  }
};

export const setSentryWorkspace = (workspace?: Workspace | null) => {
  if (isInitialized() && workspace) {
    setTag("workspace_id", workspace.id);
  }
};
